<template>
  <div class="flex flex-col items-center">
    <UiHeading type="h4" class="m-2">{{ $t('productList.filters.title') }}</UiHeading>
    <UiAccordionItem
      v-for="(filter, i) in categoryStore.filters"
      :id="`filter-${filter.key}`"
      :key="filter.key"
      :title="filter.label"
      :opened-by-default="i === 0"
    >
      <ProductListFiltersRange
        v-if="filter.attributes[0]?.type.name === FilterTypeName.RANGE"
        :filter="filter"
        :value="currentFilters.get(filter.key) as RangeFilter"
        :update-value="updateRangeFilter"
      />
      <ProductListFiltersBoolean
        v-if="filter.attributes[0]?.type.name === FilterTypeName.BOOLEAN"
        :filter="filter"
        :value="currentFilters.get(filter.key) as string[]"
        :update-value="updateArrayFilter"
      />
      <ProductListFiltersNumber
        v-if="filter.attributes[0]?.type.name === FilterTypeName.NUMBER"
        :filter="filter"
        :value="currentFilters.get(filter.key) as string[]"
        :update-value="updateArrayFilter"
      />
      <ProductListFiltersEnum
        v-if="filter.attributes[0]?.type.name === FilterTypeName.ENUM"
        :filter="filter"
        :value="currentFilters.get(filter.key) as string[]"
        :update-value="updateArrayFilter"
      />
      <ProductListFiltersString
        v-if="filter.attributes[0]?.type.name === FilterTypeName.STRING"
        :filter="filter"
        :value="currentFilters.get(filter.key) as string[]"
        :update-value="updateArrayFilter"
      />
      <UiDivider class="my-6" />
    </UiAccordionItem>
    <div class="sticky mt-4 flex w-full flex-col gap-4 border-t border-neutral-300 bg-white pt-6 xl:flex-row">
      <UiButton id="apply-filters" :loading="isLoading" @click="applyFilters">
        {{ $t('productList.filters.apply') }}
      </UiButton>
      <UiButton id="clear-filters" variant="secondary" :disabled="isLoading" @click="clearFilters">
        {{ $t('productList.filters.clear') }}
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  ProductListFiltersProps,
  ProductsFilterOptions,
  RangeFilter,
} from '~/components/ProductList/Filters/types';

import { useCategoryStore } from '~/stores/category/categoryStore';
import { FilterTypeName } from '~/utils/apiClient';

const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();

const categoryStore = useCategoryStore();

const currentFilters = ref<Map<string, RangeFilter | string[]>>(new Map<string, RangeFilter | string[]>());

const updateArrayFilter = (key: string, value: string): void => {
  const filtersValue = currentFilters.value;
  const filtersValueElements = filtersValue.get(key) as string[] | undefined;
  if (filtersValueElements?.includes(value)) {
    if (filtersValueElements?.length === 1) {
      filtersValue.delete(key);
      currentFilters.value = filtersValue;
    } else {
      currentFilters.value = filtersValue.set(
        key,
        filtersValueElements.filter((f) => f !== value),
      );
    }
  } else {
    currentFilters.value = filtersValue.set(key, [...(filtersValueElements ?? []), value]);
  }
};

const updateRangeFilter = (key: string, value: RangeFilter | null): void => {
  const filtersValue = currentFilters.value;
  if (value) {
    currentFilters.value = filtersValue.set(key, value);
  } else {
    filtersValue.delete(key);
    currentFilters.value = filtersValue;
  }
};

const props = defineProps<ProductListFiltersProps>();

const applyFilters = (): void => {
  categoryStore.setCurrentFilters(Object.fromEntries(currentFilters.value) as ProductsFilterOptions);
  props.close?.();

  dataLayer.push({
    component: 'filters',
    event: 'button_click',
    value: categoryStore.currentFilters,
  });
};

const clearFilters = (): void => {
  currentFilters.value = new Map();
  categoryStore.setCurrentFilters(null);
  props.close?.();

  dataLayer.push({
    component: 'filters',
    event: 'button_click',
    value: 'clear_filters',
  });
};
</script>
